import { BellRing, Check, ExternalLink, RotateCcw } from 'lucide-react'
import { useLocalStorage } from '../hooks/useLocalStorage'
import type { ProgramContent } from '../types/content'

interface PrepareViewProps {
  program: ProgramContent
  onOpenSettings: () => void
}

const checklistItems = [
  { id: 'age', title: '자녀 나이 확인', detail: '신청 기준일에 만 6~18세인지 확인합니다.' },
  { id: 'residency', title: '경기도 거주 여부 확인', detail: '신청일 기준 주민등록상 주소를 확인합니다.' },
  { id: 'transport-card', title: '사용 교통카드 확인', detail: '수도권 대중교통 이용 시 태깅한 카드를 준비합니다.' },
  { id: 'local-currency', title: '지역화폐 정보 준비', detail: '지원금 수령에 필요한 지역화폐 정보를 확인합니다.' },
  { id: 'duplicate', title: '중복 지원 사업 확인', detail: '같은 분기에 받은 다른 교통비 지원이 있는지 확인합니다.' },
]

export default function PrepareView({ program, onOpenSettings }: PrepareViewProps) {
  const [checked, setChecked] = useLocalStorage<Record<string, boolean>>('gg-transport-checklist', {})
  const completed = checklistItems.filter((item) => checked[item.id]).length
  const progress = Math.round((completed / checklistItems.length) * 100)

  const toggle = (id: string) => {
    setChecked((current) => ({ ...current, [id]: !current[id] }))
  }

  return (
    <div className="view-stack">
      <section className="progress-card">
        <div className="progress-card__top">
          <div>
            <p className="eyebrow">신청 준비</p>
            <h2>{completed} / {checklistItems.length} 완료</h2>
          </div>
          <strong>{progress}%</strong>
        </div>
        <div className="progress" role="progressbar" aria-label="신청 준비 진행률" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${progress}%` }} />
        </div>
        <p>체크 상태는 이 기기에만 저장됩니다.</p>
      </section>

      <section aria-labelledby="checklist-title">
        <div className="section-heading section-heading--row">
          <div>
            <p className="eyebrow">준비 항목</p>
            <h2 id="checklist-title">하나씩 확인해 보세요</h2>
          </div>
          {completed > 0 && (
            <button type="button" className="text-button" onClick={() => setChecked({})}>
              <RotateCcw aria-hidden="true" size={15} /> 초기화
            </button>
          )}
        </div>
        <ul className="checklist">
          {checklistItems.map((item) => {
            const isChecked = Boolean(checked[item.id])
            return (
              <li key={item.id}>
                <button type="button" className={`checklist-item ${isChecked ? 'is-checked' : ''}`} onClick={() => toggle(item.id)} aria-pressed={isChecked}>
                  <span className="checklist-item__check">{isChecked && <Check aria-hidden="true" size={18} />}</span>
                  <span><strong>{item.title}</strong><small>{item.detail}</small></span>
                </button>
              </li>
            )
          })}
        </ul>
      </section>

      <button type="button" className="button button--secondary" onClick={onOpenSettings}>
        <BellRing aria-hidden="true" size={18} />
        마감 알림 설정
      </button>
      <a className="button button--official" href={program.applicationUrl} target="_blank" rel="noreferrer">
        공식 사이트에서 신청하기
        <ExternalLink aria-hidden="true" size={18} />
      </a>
    </div>
  )
}
